import { useEffect, useRef, useState } from "react";

import { Input } from "~/components/ui/input";

/**
 * A text box whose value lives in the URL, committed once typing settles.
 *
 * Both list screens put their free-text filters in the address bar so a filtered
 * view is a link. Committing per keystroke would put a history entry and a
 * request behind every character of an order id, so the value is held locally
 * and pushed on a debounce.
 *
 * The callback is held in a ref rather than listed as an effect dependency: it
 * is an inline arrow at every call site, so its identity changes on every parent
 * render, and depending on it would re-arm the timer each time the table
 * refetched -- occasionally swallowing the commit entirely.
 */
export function DebouncedInput({
  value,
  onCommit,
  delay = 300,
  placeholder,
  className,
}: {
  /** The committed value, from the URL. This is the source of truth. */
  value: string;
  onCommit: (value: string) => void;
  delay?: number;
  placeholder?: string;
  className?: string;
}) {
  const [draft, setDraft] = useState(value);

  const commit = useRef(onCommit);
  commit.current = onCommit;

  // The URL wins: the back button, or a clear-all control, has to be able to
  // reset what is in the box.
  useEffect(() => setDraft(value), [value]);

  useEffect(() => {
    if (draft === value) return;
    const timer = setTimeout(() => commit.current(draft.trim()), delay);
    return () => clearTimeout(timer);
  }, [draft, value, delay]);

  return (
    <Input
      value={draft}
      onChange={(event) => setDraft(event.target.value)}
      placeholder={placeholder}
      className={className}
    />
  );
}
